import { usePreview } from "@core/hooks/usePreview";
import { getModelHash, ModelData } from "@/data/data";
import { ARButton } from "@components/ARButton";
import { PreviewInteractionHint } from "@components/PreviewInteractionHint";
import GreenImage1 from "@assets/turtle-images/green-1.webp";
import GreenTurtleVideo from "@assets/turtle-images/green-sea-turtle-2.mp4";
import GreenTurtlePoster from "@assets/turtle-images/green-sea-turtle-2-poster.webp";

interface GreenTurtleSectionProps {
  model: ModelData;
  onLaunchAR: (model: ModelData) => void;
  onTrackLaunchClick?: (
    model: ModelData,
    options?: { transportType?: "beacon" }
  ) => void;
  launchUrl?: string;
  isChecking: boolean;
  isCompatible: boolean;
}

export function GreenTurtleSection({
  model,
  onLaunchAR,
  onTrackLaunchClick,
  launchUrl,
  isChecking,
  isCompatible,
}: GreenTurtleSectionProps) {
  const { canvasRef, isFullscreen } = usePreview(model.id);

  const handleLaunchAR = (selectedModel: ModelData) => {
    if (selectedModel.arType === "webxr" && isCompatible) {
      console.log("[View AR / App Clip] launchUrl:", launchUrl);
      onLaunchAR(selectedModel);
    }
  };

  const greenCopy = {
    row1: {
      left: {
        title: "Our most common visitor",
        paragraphs: [
          {
            text: "Almost 50,000 green turtles call the Fraser Coast home, ranging from young turtles only 40 cm long to adults longer than 1 metre.",
          },
          {
            text: "Despite their numbers, green turtles are listed as vulnerable in Queensland because of the risks they face from predators, people’s activities and climate change.",
          },
          {
            text: "View the green turtle in augmented reality (AR). Can you see the missing flipper and marks on the shell of this green turtle? It was likely attacked by a shark or entangled in fishing gear. Tiger sharks are the only natural predator of sea turtles.",
            weight: 500,
          },
          {
            text: "Green turtles travel huge distances between feeding grounds (Papua New Guinea, Solomon Islands, Vanuatu, Fiji, New Caledonia & northern Australia) and nesting beaches on the Fraser Coast.",
          },
        ],
      },
      right: {
        title: "Boaties, go slow for the greens below",
        paragraphs: [
          {
            text: "We don’t see green turtles when they’re resting or sleeping as they can hold their breath for up to 7 hours.",
          },
          {
            text: "But when they’re swimming and looking for food, they typically surface for air every few minutes. This puts them at huge risk of being hit by a boat or propellor – many die from these injuries.",
          },
          {
            text: "Boaties: you can keep turtles safe, especially during nesting in summer:",
          },
        ],
        bullets: [
          "stay in deeper water away from seagrass meadows and coral",
          "slow down in shallow water – follow ‘Go Slow’ zones",
          "have someone with polarised sunglasses watch for turtles",
          "install propeller guards to prevent fatal injuries",
          "report an injured sea turtle immediately",
        ],
      },
    },
    row2: {
      left: {
        title: "Saving Sango twice",
        paragraphs: [
          {
            text: "Imagine carrying 1 kg of muck, mud and broken bone in a deep shell wound! This was the fate of Sango, a female green turtle, after her rescue in 2022. She was likely hit by a boat propeller.",
          },
          {
            text: "After 3 months of treatment and rescue, Sango was finally released back into the wild.",
          },
          {
            text: "Just over a year later, Turtles in Trouble found Sango again, stranded with signs of soft-shell syndrome. After another 3 months of treatment, she was released again.",
          },
        ],
      },
      right: {
        title: "Green fat, not shells",
        paragraphs: [
          {
            text: "Green turtles are not named for their shell colour, but because their body fat turns green from eating seagrass and algae.",
          },
          {
            text: "Green turtles are the lawnmowers of seagrass meadows, keeping the meadows healthy. But seagrass suffers if silt or poor water quality blocks sunlight. This can happen because of runoff and coastal developments.",
          },
          {
            text: "Poor water quality can also cause soft-shell syndrome in green turtles, but it’s not clear why. UniSC and collaborators are working to find the answers.",
          },
        ],
      },
    },
  };

  const renderModelViewer = () => (
    <div className="w-full relative h-[300px] sm:h-[400px] md:h-[549px]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-[60]"
        tabIndex={-1}
        style={{ width: "100%", height: "100%", outline: "none" }}
      />
    </div>
  );

  const renderFullscreenUI = () => {
    if (!isFullscreen) return null;
    return (
      <div className="fixed inset-0 z-[1000] pointer-events-none animate-fade-in">
        <div className="absolute top-4 left-4 text-white text-lg font-bold drop-shadow-lg">
          Green turtle
        </div>
        <PreviewInteractionHint />
      </div>
    );
  };

  const renderSectionBody = (section: {
    title: string;
    paragraphs: Array<{ text: string; weight?: number }>;
    bullets?: string[];
  }) => (
    <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
      <h3
        className="text-black text-[20px] sm:text-[24px] 2xl:text-[28px]"
        style={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          lineHeight: "1.2em",
        }}
      >
        {section.title}
      </h3>
      <div className="flex flex-col gap-2 sm:gap-3">
        {section.paragraphs.map((paragraph, i) => (
          <p
            key={i}
            className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: paragraph.weight || 300,
              lineHeight: "1.2em",
              whiteSpace: "pre-line",
            }}
          >
            {paragraph.text}
          </p>
        ))}
        {section.bullets && section.bullets.length > 0 ? (
          <ul
            className="list-disc pl-4 md:pl-6 marker:text-[0.7em] text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 300,
              lineHeight: "1.2em",
            }}
          >
            {section.bullets.map((bullet, i) => (
              <li key={i} className={i < section.bullets!.length - 1 ? "mb-2" : ""}>
                {bullet}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );

  return (
    <section
      id={getModelHash(model)}
      className="section-lazy relative overflow-hidden py-12 px-6 sm:py-16 sm:px-10 md:py-20 md:px-20 2xl:py-[120px] 2xl:px-[155px]"
      style={{ backgroundColor: "#F1F1F1" }}
    >
      {renderFullscreenUI()}

      <div className="flex flex-col w-full max-w-[1130px] mx-auto gap-8 sm:gap-10 md:gap-[60px]">
        <div className="flex flex-col gap-1 sm:gap-2 md:gap-2 2xl:gap-3">
          <h2
            className="text-black text-left font-[600] text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px]"
            style={{
              lineHeight: "1em",
              letterSpacing: "-0.72px",
            }}
          >
            Green turtle
          </h2>
          <h3 className="leading-[1rem] font-light text-[18px] sm:text-[20px] md:text-[24px] 2xl:text-[30px] italic">
            {model.scientificName}
          </h3>
        </div>

        <div className="flex flex-col gap-6 sm:gap-[30px]">
          <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
            {renderModelViewer()}
            <a
              href="https://sketchfab.com/3d-models/model-46a-subadult-green-sea-turtle-79d10dc4aa45412ca40f7cf6f099230c"
              target="_blank"
              rel="noopener noreferrer"
              className="text-black text-[12px] font-light leading-[1.2em] italic underline hover:opacity-70"
            >
              sketchfab
            </a>
          </div>
          <div className="w-full flex justify-center">
            <ARButton
              model={model}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={onTrackLaunchClick}
              isChecking={isChecking}
              isCompatible={isCompatible}
            />
          </div>
        </div>

        <div className="flex flex-col gap-10 sm:gap-[50px]">
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1 flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              {renderSectionBody(greenCopy.row1.left)}
            </div>
            <div className="flex-1 flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              {renderSectionBody(greenCopy.row1.right)}
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1 flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <div className="w-full h-auto">
                  <img
                    src={GreenImage1}
                    width={1090}
                    height={530}
                    alt="Green turtle rescue story"
                    className="w-full h-full"
                    loading="lazy"
                  />
                </div>
                <p
                  className="text-black text-[12px] font-light leading-[1.2em] italic"
                >
                  Photo by TITR
                </p>
              </div>
              {renderSectionBody(greenCopy.row2.left)}
            </div>
            <div className="flex-1 flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
              <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
                <div className="w-full overflow-hidden bg-gray-300" style={{ aspectRatio: "1090 / 530" }}>
                  <video
                    src={GreenTurtleVideo}
                    poster={GreenTurtlePoster}
                    className="w-full h-full object-cover"
                    controls
                    playsInline
                    preload="metadata"
                  />
                </div>
                <p
                  aria-hidden="true"
                  className="hidden text-black text-[12px] font-light leading-[1.2em] italic md:block md:invisible"
                >
                  Photo by TITR
                </p>
              </div>
              {renderSectionBody(greenCopy.row2.right)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
